import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "../components/ui/drawer";
import { api, formatApiErrorDetail } from "../lib/api";
import { getCurrencySymbol } from "../lib/currency";

const FIELD = "w-full rounded-[12px] border border-[#E7E5E4] bg-white px-3 py-2.5 text-base text-[#1C1917] focus:outline-none focus:border-[#1E3A8A]";

// iter 264: tap a quote line on mobile → edit description / specification /
// price / remark. Description, specification and remark are quote-only
// product overrides; price is the line's final_price.
export function QuoteItemEditSheet({ item, cart, onOpenChange, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ description: "", specification: "", price: "", remark: "", sample_qty: "", ci_reserved_qty: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!item) return;
    setForm({
      description: item.description || "",
      specification: item.specification || "",
      price: item.final_price != null ? String(item.final_price) : "",
      remark: item.remark || "",
      sample_qty: item.sample_qty ? String(item.sample_qty) : "",
      ci_reserved_qty: item.ci_reserved_qty ? String(item.ci_reserved_qty) : "",
    });
  }, [item]);

  if (!item) return null;
  const symbol = getCurrencySymbol(cart?.currency);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      // iter 277: reserve first — it is the only call that can be rejected (over limit), avoiding partial saves.
      const rq = form.ci_reserved_qty === "" ? 0 : Math.max(0, parseInt(form.ci_reserved_qty, 10) || 0);
      if (rq !== (item.ci_reserved_qty || 0)) await api.put(`/carts/${cart.id}/items/${item.item_id}/reserve`, { qty: rq });
      await api.put(`/carts/${cart.id}/product-overrides/${item.product_id}`, {
        description: form.description,
        specification: form.specification,
        remark: form.remark,
      });
      const price = form.price.trim() === "" ? null : Number(form.price);
      const itemPatch = {};
      if (price != null && !Number.isNaN(price) && price !== item.final_price) itemPatch.final_price = price;
      const sq = form.sample_qty === "" ? 0 : Math.max(0, parseInt(form.sample_qty, 10) || 0);
      if (sq !== (item.sample_qty || 0)) itemPatch.sample_qty = sq;
      if (Object.keys(itemPatch).length) await api.put(`/carts/${cart.id}/items/${item.item_id}`, itemPatch);
      toast.success(t("mobile.saved"));
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("mobile.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open={!!item} onOpenChange={onOpenChange}>
      <DrawerContent className="mobile-app rounded-t-[28px] border-[#E7E5E4] max-h-[92vh]" data-testid="mobile-quote-item-edit-sheet">
        <div className="overflow-y-auto">
          <DrawerHeader className="text-left pb-2">
            <DrawerTitle className="font-display text-xl text-[#1C1917]">{t("mobile.edit_item")}</DrawerTitle>
            <p className="text-xs text-[#78716C] font-mono">{item.item_no} &middot; {item.size || "-"}</p>
          </DrawerHeader>
          <div className="px-5 space-y-4 pb-4">
            <label className="block">
              <span className="text-xs font-semibold tracking-wide text-[#78716C] uppercase">{t("mobile.description")}</span>
              <textarea rows={2} value={form.description} onChange={set("description")} className={`${FIELD} mt-1 resize-none`} data-testid="mobile-edit-description" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold tracking-wide text-[#78716C] uppercase">{t("mobile.specification")}</span>
              <textarea rows={2} value={form.specification} onChange={set("specification")} className={`${FIELD} mt-1 resize-none`} data-testid="mobile-edit-specification" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold tracking-wide text-[#78716C] uppercase">{t("mobile.price")} ({symbol})</span>
              <input type="number" inputMode="decimal" step="0.01" value={form.price} onChange={set("price")} className={`${FIELD} mt-1 font-mono`} data-testid="mobile-edit-price" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold tracking-wide text-[#78716C] uppercase">{t("samples.sample_qty")}</span>
              <input type="number" inputMode="numeric" min="0" value={form.sample_qty} onChange={set("sample_qty")} placeholder="0" className={`${FIELD} mt-1 font-mono`} data-testid="mobile-edit-sample-qty" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold tracking-wide text-[#78716C] uppercase">{t("ci.reserve_qty")} <span className="normal-case font-normal">· CI {item.ci_inventory_qty ?? "-"} · {t("ci.available")} {item.ci_available ?? "-"}</span></span>
              <input type="number" inputMode="numeric" min="0" value={form.ci_reserved_qty} onChange={set("ci_reserved_qty")} placeholder="0" className={`${FIELD} mt-1 font-mono`} data-testid="mobile-edit-reserve-qty" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold tracking-wide text-[#78716C] uppercase">{t("mobile.remark")}</span>
              <textarea rows={2} value={form.remark} onChange={set("remark")} className={`${FIELD} mt-1 resize-none`} data-testid="mobile-edit-remark" />
            </label>
          </div>
          <div className="px-5 pb-8 pt-1 safe-bottom flex gap-3">
            <button type="button" onClick={() => onOpenChange(false)} className="press-scale flex-1 h-12 rounded-[14px] border border-[#E7E5E4] bg-white text-base font-medium text-[#1C1917]" data-testid="mobile-edit-cancel">{t("mobile.cancel")}</button>
            <button type="button" onClick={save} disabled={saving} className="press-scale flex-[2] h-12 rounded-[14px] bg-[#1E3A8A] text-white text-base font-semibold disabled:opacity-70" data-testid="mobile-edit-save">{t("mobile.save")}</button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
